import { api } from "api";
import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import Select from "components/shared/inputs/select";
import { addNotification } from "store/componentsSlice";
import { useAppDispatch } from "store/hooks";
import { useGetDataSourcesQuery, useGetEnvironnementsQuery } from "store/api";
import { downloadBlob } from "utils/helpers";
import useTranslation from "utils/hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";

type TraceWidgetProps = {
  onClose?: () => void;
  fixedDataSource?: {
    id?: number | string;
    name?: string;
    baseUrl?: string;
  } | null;
};

const TRACE_SOURCE_RE = /trace/i;

const toDateTimestamp = (dateValue: string, includeDayEnd: boolean): number | "" => {
  if (!dateValue) return "";
  const parsed = new Date(dateValue).getTime();
  if (Number.isNaN(parsed)) return "";
  return includeDayEnd ? parsed + 86400000 : parsed;
};

const appendFilterPath = (url: string, query: string): string => {
  if (!query) return url;
  const normalizedUrl = String(url || "").replace(/\/+$/, "");
  return `${normalizedUrl}/${query}`;
};

const TraceWidget: React.FC<TraceWidgetProps> = ({ onClose, fixedDataSource }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { data: dataSources = [], isLoading: isDataSourcesLoading } = useGetDataSourcesQuery("");
  const { data: environnements = [], isLoading: isEnvsLoading } = useGetEnvironnementsQuery("");

  const traceDataSources = useMemo(() => {
    return (Array.isArray(dataSources) ? dataSources : []).filter((item: any) => {
      const source = `${item?.name || ""} ${item?.baseUrl || ""}`;
      return TRACE_SOURCE_RE.test(source);
    });
  }, [dataSources]);

  const environmentOptions = useMemo(() => {
    return (Array.isArray(environnements) ? environnements : [])
      .filter((item: any) => item?.id != null)
      .map((item: any) => ({
        value: Number(item.id),
        text: item?.country || item?.code || `Env #${item.id}`,
      }));
  }, [environnements]);

  const [currentEnvId, setCurrentEnvId] = useState<number | null>(null);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [noSolution, setNoSolution] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    if (currentEnvId != null) return;
    const stored = localStorage.getItem("_env_id");
    if (stored != null && stored !== "" && !Number.isNaN(Number(stored))) {
      setCurrentEnvId(Number(stored));
      return;
    }
    if (environmentOptions.length > 0) {
      setCurrentEnvId(Number(environmentOptions[0].value));
    }
  }, [currentEnvId, environmentOptions]);

  const selectedDataSource = useMemo(() => {
    if (fixedDataSource?.baseUrl) return fixedDataSource;
    if (traceDataSources.length > 0) return traceDataSources[0];
    return (Array.isArray(dataSources) ? dataSources : [])[0] ?? null;
  }, [dataSources, fixedDataSource, traceDataSources]);

  const onExport = async () => {
    if (!selectedDataSource?.baseUrl) {
      dispatch(
        addNotification({
          type: "warning",
          message: t("Please select a datasource"),
        })
      );
      return;
    }

    const envId = currentEnvId ?? "";
    const start = toDateTimestamp(dateStart, false);
    const end = toDateTimestamp(dateEnd, true);
    const query = `dateStart=${start}&dateEnd=${end}&env=${envId}&noSolution=${noSolution}`;
    const endpoint = appendFilterPath(String(selectedDataSource.baseUrl), query);

    try {
      setIsExporting(true);
      const response = await api.get(endpoint, { responseType: "blob" });
      const blob = response?.data instanceof Blob ? response.data : new Blob([response.data]);
      downloadBlob(blob, "projipac4_export.xlsx");
    } catch (error: any) {
      const errorMessage =
        typeof error?.message === "string" && error.message.length > 0
          ? error.message
          : t("EXPORT_ERROR");
      dispatch(
        addNotification({
          type: "error",
          message: t("EXPORT_ERROR"),
          description: errorMessage,
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <group data-direction="column" data-gap="20" data-space="20" data-justify="start" data-wrap="no">
      <group data-justify="between" data-align="center" data-wrap="no" data-gap="10">
        <group data-gap="12" data-wrap="wrap" data-width="autofit">
          <text data-weight="600">{t("Filtrer les traces")}</text>
        </group>

        <group data-justify="end" data-gap="10" data-width="autofit" data-wrap="no">
          {onClose && (
            <Button type="button" secondary large outline onClick={onClose} disabled={isExporting}>
            {t("Back")}
          </Button>
          )}
          <Button type="button" accent large onClick={onExport} disabled={isExporting || isDataSourcesLoading}>
          {isExporting ? t("Loading") : t("Exporter")}
          </Button>
        </group>
      </group>

        <group data-gap="12" data-wrap="wrap" data-width="auto">
            <Select
                label={t("Environnement")}
                size="large"
                allowSearch
                dataLength="auto"
                dataMaxLength="310"
                value={currentEnvId as any}
                options={environmentOptions as any}
                onChange={(value: any) => {
                  if (value == null || value === "") {
                    setCurrentEnvId(null);
                    return;
                  }
                  setCurrentEnvId(Number(value));
                }}
                disabled={isEnvsLoading || isExporting}
                translateLabel={false}
            />
        </group>

        <group data-gap="12" data-wrap="wrap">
            <Input
                label={t("Date début")}
                type="date"
                size="large"
                value={dateStart}
                onChange={(e: any) => setDateStart(e.target.value)}
                disabled={isExporting}
                translateLabel={false}
            />
            <Input
                label={t("Date Fin")}
                type="date"
                // type="datepicker"
                // dataLength="300"
                size="large"
                value={dateEnd}
                onChange={(e: any) => setDateEnd(e.target.value)}
                disabled={isExporting}
                translateLabel={false}
            />
        </group>

        <Input
            type="Checkbox"
            label={t("Etudes sans solutions")}
            checked={noSolution}
            onChange={(e: any) => setNoSolution(Boolean(e?.target?.checked))}
            disabled={isExporting}
            translateLabel={false}
        />
    </group>
  );
};

export default TraceWidget;